import { Badge } from '@vrplatform/components';
import type { ResponseData } from '@vrplatform/data';
import type { ThemeColor } from '@vrplatform/theme';
import { type Maybe, formatCurrency, toTitleCase } from '@vrplatform/utils';

type PaidStatus = ResponseData<'get:/reservations/{id}'>['payment']['status'];

type Props = {
  paidStatus: Maybe<PaidStatus>;
  paymentsCentTotal: number;
  reservationCentTotal: number;
  currency: string | undefined;
};

const OPTIONS: Record<
  PaidStatus,
  {
    label: string;
    color: ThemeColor;
  }
> = {
  unpaid: {
    label: 'Unpaid',
    color: 'red',
  },
  underpaid: {
    label: 'Underpaid',
    color: 'yellow',
  },
  overpaid: {
    label: 'Overpaid',
    color: 'red',
  },
  paid: {
    label: 'Paid',
    color: 'green',
  },
  coHost: {
    label: 'Co-host',
    color: 'blue',
  },
};

export const ReservationPaymentStatusBadge = ({
  paidStatus,
  currency,
  paymentsCentTotal,
  reservationCentTotal,
}: Props) => {
  if (!paidStatus) return null;

  const { label, color } = OPTIONS[paidStatus] || {
    color: 'gray',
    label: toTitleCase(paidStatus),
  };

  const balance = reservationCentTotal - paymentsCentTotal;

  if (paidStatus === 'paid') {
    return <Badge color={color}>Paid</Badge>;
  }

  if (paidStatus === 'unpaid') {
    return <Badge color={color}>Unpaid</Badge>;
  }

  if (paidStatus === 'overpaid') {
    return (
      <Badge color={color}>
        Overpaid ({formatCurrency(balance / 100, currency)})
      </Badge>
    );
  }

  if (paidStatus === 'underpaid') {
    return (
      <Badge color={color} tooltip="Not fully paid">
        {formatCurrency(balance / 100, currency)}
      </Badge>
    );
  }

  return <Badge color={color}>{label}</Badge>;
};
